import { ComponentType, lazy, useEffect, useState } from "react";
import { USER } from "./user";

type PopinModule = Promise<{ default: ComponentType<any> }>;
type PopinLoader = () => PopinModule;

function createPopinLoader(loadPopin: PopinLoader): PopinLoader {
  let popinModule: PopinModule | undefined;
  return () => popinModule ??= loadPopin();
}

const popinLoaders: Record<popinsNames, PopinLoader> = {
  login: createPopinLoader(() => import("../Popins/Login")),
  viewer: createPopinLoader(() => import("../Popins/EphemeViewer")),
  tokens: createPopinLoader(() => import("../Popins/Tokens")),
  disclaimer: createPopinLoader(() => import("../Popins/Disclaimer")),
  subscribe: createPopinLoader(() => import("../Popins/Subscription")),
  certificate: createPopinLoader(() => import("../Popins/Certificate")),
  edit: createPopinLoader(() => import("../Popins/EditProfile")),
  uploading: createPopinLoader(() => import("../Popins/Uploading")),
  contact: createPopinLoader(() => import("../Popins/Contact")),
  trophy: createPopinLoader(() => import("../Popins/Trophy")),
  blocked: createPopinLoader(() => import("../Popins/Blocked")),
  contentprovider: createPopinLoader(() => import("../Popins/ContentProvider")),
  paymentrequired: createPopinLoader(() => import("../Popins/PaymentRequired")),
  emailconfirm: createPopinLoader(() => import("../Popins/EmailConfirm")),
  chatcontent: createPopinLoader(() => import("../Popins/ChatContent")),
  chatreport: createPopinLoader(() => import("../Popins/ChatReport")),
  underage: createPopinLoader(() => import("../Popins/Underage")),
  fanclub: createPopinLoader(() => import("../Popins/Fanclub")),
  chaterror: createPopinLoader(() => import("../Popins/ChatError")),
  forcelogin: createPopinLoader(() => import("../Popins/ForceLogin")),
  commonboost: createPopinLoader(() => import("../Popins/CommonBoost")),
  pickvalidator: createPopinLoader(() => import("../Popins/Underage/pickValidator")),
  addcard: createPopinLoader(() => import("../Popins/Underage/addCard")),
  requiredtemp: createPopinLoader(() => import("../Popins/UploaderRequiredTemp")),
  paymentsuccessful: createPopinLoader(() => import("../Popins/PaymentSuccessful")),
  dailyspin: createPopinLoader(() => import("../Popins/DailySpin")),
};

const popinsObj: {
  [Key in popinsNames]: ComponentType<any>;
} = {
  login: lazy(popinLoaders.login),
  viewer: lazy(popinLoaders.viewer),
  tokens: lazy(popinLoaders.tokens),
  disclaimer: lazy(popinLoaders.disclaimer),
  subscribe: lazy(popinLoaders.subscribe),
  certificate: lazy(popinLoaders.certificate),
  edit: lazy(popinLoaders.edit),
  uploading: lazy(popinLoaders.uploading),
  contact: lazy(popinLoaders.contact),
  trophy: lazy(popinLoaders.trophy),
  blocked: lazy(popinLoaders.blocked),
  contentprovider: lazy(popinLoaders.contentprovider),
  paymentrequired: lazy(popinLoaders.paymentrequired),
  emailconfirm: lazy(popinLoaders.emailconfirm),
  chatcontent: lazy(popinLoaders.chatcontent),
  chatreport: lazy(popinLoaders.chatreport),
  underage: lazy(popinLoaders.underage),
  fanclub: lazy(popinLoaders.fanclub),
  chaterror: lazy(popinLoaders.chaterror),
  forcelogin: lazy(popinLoaders.forcelogin),
  commonboost: lazy(popinLoaders.commonboost),
  pickvalidator: lazy(popinLoaders.pickvalidator),
  addcard: lazy(popinLoaders.addcard),
  requiredtemp: lazy(popinLoaders.requiredtemp),
  paymentsuccessful: lazy(popinLoaders.paymentsuccessful),
  dailyspin: lazy(popinLoaders.dailyspin),
};

type popinsNames = "dailyspin"|"paymentsuccessful"|"requiredtemp"|"addcard"|"pickvalidator"|"commonboost"|"chaterror"|"fanclub"|"underage"|"chatreport"|"chatcontent"|"contentprovider"|"blocked"|"edit"|"login"|"paymentrequired"|"viewer"|"tokens"|"disclaimer"|"subscribe"|"certificate"|"uploading"|"contact"|"trophy"|"emailconfirm"|"forcelogin"

export function preloadPopins() {
  return Promise.allSettled(Object.values(popinLoaders).map((loadPopin) => loadPopin()));
}

export function preloadPopin(name: popinsNames) {
  return popinLoaders[name]();
}

class Popins {
  popinsList: ComponentType<any>[] = [];
  propsList: any[] = [];
  listener: any;
  display = (name: popinsNames, properties = {}) => {

    let isEmailConfirmed = USER.informations?.is_email_confirmed;
    
    if(!isEmailConfirmed && (name === "tokens" || name === "subscribe")){
      this.popinsList.push(popinsObj.emailconfirm);
      if(this.listener) this.listener({popins: this.popinsList, props: this.propsList});
      return;
    }

    this.popinsList.push(popinsObj[name]);
    this.propsList.push(properties);
    if(this.listener) this.listener({popins: this.popinsList, props: this.propsList});
  };

  close = () => {
    this.popinsList.shift();
    this.propsList.shift();
    if(this.listener) this.listener({popins: this.popinsList, props: this.propsList});
  };
}

const POPINS = new Popins();

export const closeCurrentPopin = POPINS.close;

export function displayPopin() {
  return POPINS.display;
}

(window as any).displayPopin = displayPopin;
(window as any).preloadPopin = preloadPopin;

(window as any).displayVerificationEmailPopin = () =>
  displayPopin()("emailconfirm");

export function useListenPopins() {
  const [popins, setPopins] = useState<{popins: ComponentType<any>[], props: any[]}>({popins: POPINS.popinsList, props: POPINS.propsList});

  useEffect(() => {
    POPINS.listener = setPopins;
  }, []);

  return popins;
}
